import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import TablePageNavigation from '../../../../components/TablePageNavigation'
import { useConnectionsStore } from '../../../../stores/connections/useConnectionsStore'
import { useConnectionsActions } from '../../../../stores/connections/useConnectionsActions'
import Row from '../../../../components/Row'
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers'
import FilterButton from '../../../../fragments/FilterButton'
import { AppRoutes, SidebarTypes } from '../../../../interfaces/store/appStore'
import Button from '../../../../components/Button'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { useNavigate } from 'react-router-dom'
import ConnectionSearchFilter from '../../../../fragments/ConnectionSearchFilter'
import { useSaveFilterHandlers } from '../../../../services/actions/save-filters/useSaveFilterHandlers'
import ConnectionCustomizeColumns from '../../../../fragments/ConnectionCustomizeColumns'
import { IConnection } from '../../../../interfaces/models/connection'
import BulkConnectionActionsDropdown from '../../../../fragments/dropdowns/BulkConnectionActionsDropdown'
import Column from '../../../../components/Column'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'
import { hasCampaignsAccess } from '../../../../services/campaigns/campaignsNavAccess'
import { useCampaignsBulkActionAnnouncement } from '../../../../services/campaigns/useCampaignsBulkActionAnnouncement'
import { CONNECTION_ACTION_BAR_BP } from './connectionActionBarBreakpoints'
import ConnectionFilterLabels from '../../../../fragments/ConnectionFilterLabels'
import { useAppStore } from '../../../../stores/application/useAppStore'
import { styles } from '../../../../styles/themes/style'
import { useAnalyticsHandlers } from '../../../../services/actions/analytics/useAnalyticsHandlers'
import posthog from 'posthog-js';
import { isLNFirst } from './../../../../utils';
import Confetti from "react-confetti";
import { IEnrichedTransaction } from '../../../../components/modal/modals/extension/CalculateEnrichmentModal'
import QuickActions from './QuickActions'
import { useHubspotHandlers } from '../../../../services/actions/hubspot/useHubspotHandlers'
import { useAuthHandlers } from '../../../../services/actions/auth/useAuthHandlers'
import momentTimezone from 'moment-timezone';

const connectionIdsFromRows = (rows: any[] | undefined): string[] =>
    (rows ?? []).flatMap((row) => {
        if (typeof row === 'string' || typeof row === 'number') return [String(row)];
        if (row?._id) return [String(row._id)];
        return [];
    });

interface IConnectionControlBarProps { 
    isMobile?: any,
    isSmall?: any,
    selectedConnections: IConnection[],
    selectedAllConnections?: boolean,
    isListView?: boolean,
    onBulkEnrich?: (selectedConnections: IConnection[], privateEmailChecked: boolean, businessEmailChecked: boolean, phoneChecked: boolean, enrichedTransactionId: string) => void;
    onEnrichedTransactionChange?: (enrichedTransaction: IEnrichedTransaction) => void;  
    onPageChanged?: (page: number) => void;
    onPageSizeChanged?: (size: number) => void;
    onSetSavedFilter?: () => void;
    onResetSavedFilter?: (e: any) => void;
    onClearSelection?: () => void;
}

interface IRowStyle {
    showMobile?: boolean
}

interface IAnalytics {
    "connections-updated-connected-to-linkedin": number
}

const ConnectionControlBar: FunctionComponent<IConnectionControlBarProps> = ({selectedConnections, selectedAllConnections, isListView, onBulkEnrich, onEnrichedTransactionChange, onPageChanged, onPageSizeChanged, onSetSavedFilter, onClearSelection}) => {
    const navigate = useNavigate()
    const { store: { user, isLoggedToLinkedin, trial, disconnectedConnectionsCount, workspace, subscription } } = useAuthStore()
    const { store: { extensionInstalled, extensionVersion, requestLoading } } = useAppStore()
    const { store: { connectionsFilter, connectionsParameters }} = useConnectionsStore()
    const { patchConnection } = useConnectionsActions()
    const { page, pageSize, total } = connectionsParameters
    
    const isStarterPlan = workspace?.isStarter
    const hubspotEnabled = workspace?.hubspotEnabled === true
    const campaignsAllowed = hasCampaignsAccess(workspace, trial)
 
    const { setConnectionsFilterHandler, sendMessageToConnectionsHandler, exportToCSVHAndler, numberOfConnectionsFiltersHandler, setClearConnectionsFilterHandler, getConnectionsHandler, getMultipleConditionsFiltersHandler, toggleBulkHideConnectionsHandler, scheduleConnectionsRemovalHandler, getSelectedConnections, bulkAddConnectionsToKanbanHandler, openAddToCampaignFlow, generateAiRecommendationsHandler, getConnectionAiRecommendationsHandler } = useConnectionsHandlers()
    const { openSidebar, setExportConnectionsModalHandler, setScheduleDisconnectModalHandler, setConfirmationModal, hideModal, showErrorToast, showSuccessToast, showInfoToast, setCalculateEnrichmentModalHandler, setBulkEditTagsModal, setCreateFeedModal, setExportToHubspotModalHandler, setAddToKanbanModal } = useAppHandlers()
    const { searchPeopleSduiHandler } = useAuthHandlers()
    const { showCampaignsAnnouncementIfNeeded, shouldShowAnnouncementFor } = useCampaignsBulkActionAnnouncement()
    const { getSavedFiltersHandler, removeFiltersHandler } = useSaveFilterHandlers()
    const { exportToHubspotHandler } = useHubspotHandlers()

    const [savedFilters, setSavedFilters] = useState<any[]>([]);
    const [user1stDegreeSelectedConnections, setUser1stDegreeSelectedConnections] = useState<any>([])
    const [hasLinkedInFirstConnection, setHasLinkedInFirstConnection] = useState<boolean>(false)

    const [selectedConnectionsLoading, setSelectedConnectionsLoading] = useState<boolean>(false)
    const [enrichLoading, setEnrichLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [scanMutualsLoading, setScanMutualsLoading] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const scanMutualsInFlightRef = useRef(false);

    const connectionsRef = useRef(user1stDegreeSelectedConnections);

    useEffect(() => {
    connectionsRef.current = user1stDegreeSelectedConnections;
    }, [user1stDegreeSelectedConnections]);

    useEffect(() => {
        if('getSelectedConnectionsIds' in requestLoading){
            setSelectedConnectionsLoading(requestLoading?.['getSelectedConnectionsIds'])
        }
    }, [requestLoading]);

    const { getTotalConnectionsOverviewAnalytics } = useAnalyticsHandlers();

    const [ analytics, setAnalytics ] = useState<IAnalytics>({
        "connections-updated-connected-to-linkedin": 0,
    })

    const isExtensionVersionCorrect = extensionVersion === process.env.REACT_APP_VERSION

    const restrictScanMutualsTo1M = (process.env.REACT_APP_ENABLE_SCAN_MUTUALS_ONLY_FOR_1M_WORKSPACE ?? 'false').toLowerCase() === 'true';
    const oneMWorkspaceId = process.env.REACT_APP_1M_WORKSPACE_ID;
    const canShowScanMutuals = !restrictScanMutualsTo1M || (!!oneMWorkspaceId && workspace?._id === oneMWorkspaceId);
    const canUseWarmIntroAi =
        !!workspace?.aiChatEnabled ||
        !!trial ||
        (!!workspace?.isBusiness && subscription?.status === 'active');

    // Must match backend's America/New_York date logic (and optimistic updates) to keep limits in sync.
    const todayKey = momentTimezone.tz(new Date(), "America/New_York").format("YYYY-MM-DD");
    const dailyMax = Number(process.env.REACT_APP_AI_RECOMMENDATIONS_DAILY_LIMIT ?? 20) || 20;
    const usedToday = (user as any)?.aiRecommendationsAnalytics?.dailyDate === todayKey
        ? (Number((user as any)?.aiRecommendationsAnalytics?.dailyCount ?? 0) || 0)
        : 0;
    const remainingToday = Math.max(0, dailyMax - usedToday);

    // Bulk selection limit is tied directly to remaining daily limit.
    const effectiveBulkCap = remainingToday;

    const selectedForAiCount = selectedConnections?.length ?? 0;
    const isAiSelectionOverLimit = selectedForAiCount > effectiveBulkCap && effectiveBulkCap > 0;

    // Campaigns announcement overrides the bulk action buttons' disabled state so the modal can open on click.
    const bulkMessageDisabled = workspace?.isStarter || user1stDegreeSelectedConnections?.length === 0 || selectedAllConnections || user1stDegreeSelectedConnections?.length > 100 || !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect;
    const announceBulkMessage = shouldShowAnnouncementFor('bulkMessage');
    const bulkRemoveDisabled =
        (user1stDegreeSelectedConnections?.length === 0 && !selectedAllConnections)
        || !(isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect);
    const announceBulkDisconnect = shouldShowAnnouncementFor('bulkDisconnect');

    const hiddenContactsFilterValue = connectionsFilter?.hiddenConnectionsValue?.value
    /** Align with BulkConnectionActionsDropdown: only relevant bulk action stays enabled under the Hidden filter */
    const bulkHideDisabled =
        selectedConnections?.length === 0 || hiddenContactsFilterValue === 'yes';
    const bulkUnhideDisabled =
        selectedConnections?.length === 0 || hiddenContactsFilterValue === 'no';

    useEffect(() => {
        // Only call these APIs when in Table view (isListView === true), not in Kanban view
        if (isListView) {
            getConnectionsHandler();
        }
    }, [connectionsFilter, page, pageSize, isListView ]);

    useEffect(() => {
        // Only call saved-filters API when in Table view (isListView === true), not in Kanban view
        if (isListView) {
            getSavedFilters()
        }
    }, [connectionsFilter.savedFilter, isListView])

    const getSavedFilters = async () => {
        const result = await getSavedFiltersHandler()
        setSavedFilters(result ?? [])
    }

    const exportConnections = useCallback(async (includeTags: boolean, includeNotes: boolean, exportAll: boolean) => {
        if(exportAll){
            await exportToCSVHAndler(includeTags, includeNotes)
        }
        else {
            await exportToCSVHAndler(includeTags, includeNotes, selectedConnections)
        }
    }, [selectedConnections, connectionsFilter]) 

    const exportToHubspot = useCallback(async (
        includeTags: boolean,
        includeNotes: boolean,
        exportAll: boolean,
        useDefaultMapping: boolean,
        exportType: 'contacts' | 'list' | 'workflow' | 'deals',
        exportDetails?: {
            listId?: string;
            workflowId?: string;
            dealStage?: string;
            dealName?: string;
            amount?: number;
            pipelineId?: string;
        }
    ) => {
        try {
            const basePayload: any = {
                connectionIds: exportAll ? [] : selectedConnections?.map(connection => connection._id) || [],
                exportAll,
                includeTags,
                includeNotes,
                useDefaultMapping,
                exportType,
                ...exportDetails
            }

            if (useDefaultMapping) {
                const result = await exportToHubspotHandler(basePayload)

                if (result?.success) {
                    showSuccessToast({ message: result.message || 'Successfully exported to HubSpot' })
                }
                else {
                    showErrorToast({ message: 'Error exporting contact to HubSpot' });
                }
            } else {
                navigate(AppRoutes.HUBSPOT_MAPPING, {
                    state: {
                        exportParams: basePayload
                    }
                })
            }
        } catch (error) {
            console.error('Export to HubSpot error:', error)
            showErrorToast({ message: 'Error exporting contacts to HubSpot' })
        }
    }, [selectedConnections, exportToHubspotHandler, navigate, showSuccessToast, showErrorToast])

    const searchHandler = useCallback((e: any) => {
        console.log(e);
        
        const updatedFilter = {
            ...connectionsFilter,
            searchValue: e,
            searchByName: 'yes', // TODO
            sortClicked: false
        };
        
        setConnectionsFilterHandler(updatedFilter);
    }, [connectionsFilter, setConnectionsFilterHandler]); 


    const isCsvConnection = (connection: IConnection) => {
        const hasCSVOrImportedTag = connection.tags?.some(item => item.title === 'CSV' || item.title === 'Imported');
        const hasLinkedinTag = connection.tags?.some(item => item.title === 'LinkedIn' || item.title === 'LinkedIn 1st' || item.title === 'Disconnected');
    
        return hasCSVOrImportedTag && !hasLinkedinTag;
    };

    const resetFilterHandler = useCallback(() => {
        setClearConnectionsFilterHandler();
    }, [connectionsFilter, setConnectionsFilterHandler]);

    useEffect(() => {
        for (let selectedConnection of selectedConnections) {
            if (isLNFirst(selectedConnection, user?._id)) {
                setHasLinkedInFirstConnection(true)
                break
            } else {
                setHasLinkedInFirstConnection(false)
            }
        }
        let result = selectedConnections?.filter(
            (selectedConnection) =>
                isLNFirst(selectedConnection, user?._id) && // use only Ln-1st connections
                !selectedConnection?.autoTagsArray?.some((tagArrayItem: any) => tagArrayItem?._id === user?._id && // skip disconnected connections
                        tagArrayItem?.autoTags?.some(
                            (tag: any) => tag?.title === "Disconnected"
                        )
                )
        );
        setUser1stDegreeSelectedConnections(result);
    }, [selectedConnections])

    const scheduleDisconnectHandler = useCallback(async () => {
        if(user1stDegreeSelectedConnections?.length){
          scheduleConnectionsRemovalHandler(user1stDegreeSelectedConnections, !!selectedAllConnections);
        }
    }, [selectedConnections, selectedAllConnections, user1stDegreeSelectedConnections])

    const calculateEnrichmentHandler = useCallback(async (selectedConnections: IConnection[], privateEmailChecked: boolean, businessEmailChecked: boolean, phoneChecked: boolean, enrichedTransactionId: string) => {
        console.log('calculateEnrichmentHandler', selectedConnections, privateEmailChecked, businessEmailChecked, phoneChecked, enrichedTransactionId);    
        if (onBulkEnrich) onBulkEnrich(selectedConnections, privateEmailChecked, businessEmailChecked, phoneChecked, enrichedTransactionId);
    }, [])

    const onEnrichedTransactionChangeHandler = useCallback((enrichedTransaction: IEnrichedTransaction) => {
        console.log('onEnrichedTransactionChange', enrichedTransaction);
        onEnrichedTransactionChange && onEnrichedTransactionChange(enrichedTransaction);
    }, [])

    const clickOnRemoveHandler = async () => {
        if (showCampaignsAnnouncementIfNeeded('bulkDisconnect', bulkRemoveDisabled ? undefined : () => { void proceedWithRemove() })) return;
        if (bulkRemoveDisabled) return;
        await proceedWithRemove();
    }

    const proceedWithRemove = async () => {
        setRemoveLoading(true);
        try {
            let totalConnections = user1stDegreeSelectedConnections?.length;

            if (selectedAllConnections) {
                // get selected connections IDs
                let result = await getSelectedConnections(total, false, true);
                setUser1stDegreeSelectedConnections(result);
                totalConnections = result?.length;
            }

            if(trial && (disconnectedConnectionsCount + user1stDegreeSelectedConnections?.length > 25)){
                setConfirmationModal({
                    headingText: `${disconnectedConnectionsCount >= 25 ? 'Free Trial Limit Reached' : 'You Hit the Free Trial Limit'}`,
                    descriptionText: `${disconnectedConnectionsCount >= 25 
                        ? 'You’ve reached your free trial limit of 25 removed contacts. Upgrade to LeadDelta Pro or Starter to schedule more removals and take full control of your professional network.' 
                        : 'Free trial users can remove up to 25 contacts. Upgrade to LeadDelta Pro or Starter to schedule more removals and take full control of your professional network.'}`,
                    onCancelHandler: () => hideModal(),
                    onConfirmHandler: async () => {
                        hideModal();
                        window.open(AppRoutes.BILLING, '_blank');
                    },
                    confirmBtnTitle: 'Upgrade Plan'
                });

                disconnectedConnectionsCount >= 25 ? showErrorToast({message: 'Free trial limit reached.'}) : showErrorToast({message: 'You hit the free trial limit.'});
            } else {
                setScheduleDisconnectModalHandler(
                    scheduleDisconnectHandler,
                    selectedAllConnections,
                    totalConnections
                )
            }
        } finally {
            setRemoveLoading(false);
        }
    }

    const onClickEnrichHandler = async () => {
        setEnrichLoading(true);
        setShowConfetti(false);
        try {
            if (selectedAllConnections) {
                let result = await getSelectedConnections(total, true, false);
                setCalculateEnrichmentModalHandler(
                    calculateEnrichmentHandler,
                    onEnrichedTransactionChangeHandler,
                    selectedAllConnections,
                    result,
                    undefined,
                    false,
                    false,
                    false,
                    false,
                    showEnrichmentConfetti
                );
            } else {
                setCalculateEnrichmentModalHandler(
                    calculateEnrichmentHandler,
                    onEnrichedTransactionChangeHandler,
                    selectedAllConnections,
                    selectedConnections,
                    undefined,
                    false,
                    false,
                    false,
                    false,
                    showEnrichmentConfetti
                );
            }
        } finally {
            setEnrichLoading(false);
        }
    }

    // Poll until the backend AI generation completes (backend generate is fire-and-forget).
    // Returns final status + match count (AI mutual profiles length).
    const pollUntilDone = async (connectionId: string): Promise<{ status: string; matches: number }> => {
        const maxAttempts = 24; // 24 × 5 s = 2 min max
        const delayMs = 5000;
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((res) => setTimeout(res, delayMs));
            try {
                const latest: any = await getConnectionAiRecommendationsHandler(connectionId);
                // Only stop polling on terminal states. Treating "idle" as terminal can freeze UI at 0
                // while generation is still warming up.
                if (latest?.status && (latest.status === 'ready' || latest.status === 'error')) {
                    const matches = latest?.linkedinAiMutualProfilesCount ?? (Array.isArray(latest?.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0);
                    patchConnection(connectionId, {
                        aiRecommendationsStatus: latest.status as any,
                        linkedinAiMutualProfilesCount: latest.linkedinAiMutualProfilesCount ?? (Array.isArray(latest.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0)
                    } as any);
                    return { status: latest.status, matches };
                }
            } catch {
                // retry on transient errors
            }
        }
        // Timed out — show error so user can retry
        patchConnection(connectionId, { aiRecommendationsStatus: 'error' as any });
        return { status: 'error', matches: 0 };
    };

    const onClickScanMutualsHandler = async () => {
        if (scanMutualsInFlightRef.current) return;

        scanMutualsInFlightRef.current = true;
        setScanMutualsLoading(true);
        try {
            const candidates: IConnection[] = selectedAllConnections
                ? ((await getSelectedConnections(total, true, false)) as IConnection[])
                : (selectedConnections ?? []);

            const validConnections = (candidates ?? []).filter((c) => !!c?._id);
            if (validConnections.length === 0) {
                showInfoToast({ message: 'Please select contacts to generate AI recommendations.' });
                return;
            }

            const allowedCount = Math.max(0, Math.min(effectiveBulkCap, validConnections.length));
            if (allowedCount === 0) {
                showInfoToast({ message: 'Daily limit reached. Try again tomorrow.' });
                return;
            }
            const limitedConnections = validConnections.slice(0, allowedCount);
            if (validConnections.length > limitedConnections.length) {
                showInfoToast({ message: `Processing first ${limitedConnections.length} selected contacts.` });
            }

            // Optimistic UI: show "Analyzing..." in table for all selected in this run — race-condition-safe.
            // Also reset AI count so UI doesn't treat old results as ready.
            limitedConnections.forEach((c) => {
                if (c?._id) {
                    patchConnection(c._id, {
                        aiRecommendationsStatus: 'generating' as any,
                        linkedinAiMutualProfilesCount: 0
                    } as any);
                }
            });

            // Bulk should not auto-open the sidebar (confusing while processing).
            showInfoToast({ message: 'Finding mutual contacts. Results will appear shortly.' });

            // processOne: scan LinkedIn, kick off AI generation (fire-and-forget on backend),
            // then start a background poll — because generate returns immediately with 'generating'.
            const processOne = async (connection: IConnection): Promise<{ stop: boolean; poll?: Promise<{ status: string; matches: number }> }> => {
                try {
                    // 1) Scan mutual profiles (fills linkedinMutualProfiles)
                    if (extensionInstalled && isLoggedToLinkedin) {
                        try {
                            await searchPeopleSduiHandler(connection.profileId ?? '', connection.publicIdentifier ?? '', connection._id);
                        } catch (scanError) {
                            console.warn('Mutual scan failed; generating AI recommendations anyway.', scanError);
                            showInfoToast({ message: 'Mutual scan failed. Generating AI recommendations anyway.' });
                        }
                    }

                    // 2) Kick off AI generation — backend returns immediately with status 'generating'
                    await generateAiRecommendationsHandler(connection._id);

                    // 3) Poll in background until AI finishes and patch the table row
                    const poll = pollUntilDone(connection._id);
                    return { stop: false, poll };
                } catch (error: any) {
                    const status = error?.response?.status;
                    if (status === 429) {
                        showInfoToast({ message: 'Daily AI recommendations limit exceeded.' });
                        patchConnection(connection._id, { aiRecommendationsStatus: 'idle' as any });
                        return { stop: true };
                    }
                    console.error('Error while scanning mutuals / generating AI recommendations:', error);
                    showErrorToast({ message: 'Error while scanning mutuals / generating AI recommendations.' });
                    patchConnection(connection._id, { aiRecommendationsStatus: 'error' as any });
                }
                return { stop: false };
            };

            // Process sequentially in background (scans shouldn't overlap), then show one aggregated toast at the end.
            void (async () => {
                const polls: Array<Promise<{ status: string; matches: number }>> = [];
                let processedCount = 0;
                for (const connection of limitedConnections) {
                    const r = await processOne(connection);
                    if (r?.poll) {
                        polls.push(r.poll);
                        processedCount += 1;
                    }
                    if (r?.stop) break;
                }

                const results = await Promise.allSettled(polls);
                const totalMatches = results.reduce((sum, r) => {
                    if (r.status !== 'fulfilled') return sum;
                    return sum + (Number(r.value?.matches ?? 0) || 0);
                }, 0);

                if (processedCount > 0) {
                    showSuccessToast({
                        message: `${totalMatches} intro path${totalMatches === 1 ? '' : 's'} found for ${processedCount} contact${processedCount === 1 ? '' : 's'}`
                    });
                    posthog.capture('find mutuals completed', {
                        source: 'connections_control_bar',
                        bulk: true,
                        selectedCount: processedCount,
                        matchesFound: totalMatches,
                    });
                }
            })();
        } finally {
            setScanMutualsLoading(false);
            scanMutualsInFlightRef.current = false;
        }
    }

    const showEnrichmentConfetti = () => {
        setShowConfetti(true);
    }

    const getTotalConnectionsOverviewAnalyticsHandler = useCallback(async () => {
        const statistics = await getTotalConnectionsOverviewAnalytics();
        let statisticsObject = statistics.reduce(
          (accumulator, value) => ({
            ...accumulator,
            ...{ [value.version]: value.value },
          }),
          {}
        );
        setAnalytics({ ...analytics, ...statisticsObject });
        return statisticsObject;
      }, []);

    const handlePageChanged = onPageChanged || (() => {});
    const handlePageSizeChanged = onPageSizeChanged || (() => {});

    const onSetSavedFilterHandler = (savedFilter: any) => {
        setConnectionsFilterHandler({...connectionsFilter, ...savedFilter.filters});
        onSetSavedFilter && onSetSavedFilter();
    }

    const onRemoveSavedFilterHandler = async (savedFilter: any) => {
        setSavedFilters((prev: any[]) => prev.filter(filter => filter._id !== savedFilter._id));
        const result = await removeFiltersHandler(savedFilter)
        if(result.success) {
            showSuccessToast({message: result?.message});
        } else {
            showErrorToast({message: result?.message});
        }
    } 

    const handleTagFilter = (autotagsFilter: string, isAutotagsFilterOr: 'yes' | 'no', key: string) => {
        const params = {
            ...connectionsFilter,
            selectedUsersMutual: [{
                _id: user._id,
                firstName: user?.firstName,
                lastName: user?.lastName
            }],
            isAutotagsFilterOr,
            usersMutualCondition: 'Or',
            autotagsFilter,
            quickActions: key
        }
        setConnectionsFilterHandler(params)
    };

    return <Column>
              {showConfetti &&
                <>
                    <Confetti
                        width={window.innerWidth}
                        height={window.innerHeight}
                        numberOfPieces={1800}
                        recycle={false}
                        gravity={0.2}
                        initialVelocityY={25}
                        initialVelocityX={20}
                        confettiSource={{ x: 0, y: window.innerHeight - 1, w: 0, h: 0 }}
                        run={true}
                    />
                    <Confetti
                        width={window.innerWidth}
                        height={window.innerHeight}
                        numberOfPieces={1800}
                        recycle={false}
                        gravity={0.2}
                        initialVelocityY={25}
                        initialVelocityX={20}
                        confettiSource={{ x: window.innerWidth, y: window.innerHeight - 1, w: 0, h: 0 }}
                        run={true}
                    />
                </>
            }
            <Row gap='8px' alignItems spaceBetween flexWrap>
            {isListView &&
                <ShowMobile>
                    <StyledRow gap="12px" showMobile>
                        <ConnectionSearchFilter searchValue={connectionsFilter?.searchValues[0]?.searchValue} />
                    </StyledRow>
                </ShowMobile>
            }
            <StyledRow>
                <ActionButtonsRow gap="8px">
                    {isListView &&
                    <>
                        <ConnectionCustomizeColumns />
                        <ResponsiveContainerMessageButton>
                            <Button
                                $type="white"
                                disabled={bulkMessageDisabled && !announceBulkMessage}
                                $customWidth
                                $SVGtype="message"
                                $color={styles.colors.primary600}
                                $iconColor={styles.colors.black300}
                                onClickHandler={() => {
                                    const openBulkMessageSidebar = () =>
                                        openSidebar(SidebarTypes.BULK_MESSAGE, {
                                            formType: 'message',
                                            onMessageSaveHandler: (message: string) =>
                                                sendMessageToConnectionsHandler(connectionsRef?.current, message)
                                        });
                                    if (showCampaignsAnnouncementIfNeeded('bulkMessage', bulkMessageDisabled ? undefined : openBulkMessageSidebar)) return;
                                    if (bulkMessageDisabled) return;
                                    openBulkMessageSidebar();
                                }}
                                tooltipMessage={
                                    announceBulkMessage ? ''
                                    : !extensionInstalled ? 'Install Extension'
                                    : !isExtensionVersionCorrect ? 'Update extension'
                                    : !isLoggedToLinkedin ? 'Login to Linkedin'
                                    : workspace?.isStarter ? 'Upgrade to Pro to unlock this feature'
                                    : selectedAllConnections ? 'Deselect “all” to message'
                                    : user1stDegreeSelectedConnections?.length > 100 ? 'Select up to 100 contacts'
                                    : user1stDegreeSelectedConnections?.length === 0 ? 'Select contacts to message'
                                    : '' }
                            >
                                {'Message'}
                            </Button>
                        </ResponsiveContainerMessageButton>
                        {campaignsAllowed && (
                            <ResponsiveContainerCampaignsButton>
                                <Button
                                    $type="white"
                                    disabled={selectedConnections?.length === 0}
                                    $customWidth
                                    $SVGtype="campaignGray"
                                    $disabledSVGtype="campaignGrayDisabled"
                                    $color={styles.colors.primary600}
                                    $iconColor={styles.colors.black300}
                                    tooltipMessage="Add contacts to a campaign"
                                    onClickHandler={async () => {
                                        await openAddToCampaignFlow(selectedConnections, {
                                            selectedAllConnections,
                                            total,
                                            onClose: onClearSelection,
                                        });
                                    }}
                                >
                                    {'Add to campaign'}
                                </Button>
                            </ResponsiveContainerCampaignsButton>
                        )}
                        <ResponsiveContainerEnrichButton>
                            <Button
                                    $type="white"
                                    disabled={selectedConnections?.length === 0}
                                    $customWidth
                                    $SVGtype="enrichGrey"
                                    $disabledSVGtype="enrichDisabled"
                                    $color={styles.colors.primary600}
                                    tooltipMessage="Enrich contacts"
                                    onClickHandler={onClickEnrichHandler}
                                    isLoading={selectedConnectionsLoading && enrichLoading}
                                >
                                    {'Enrich'}
                            </Button>
                        </ResponsiveContainerEnrichButton>
                        {canShowScanMutuals ? (
                            <ResponsiveContainerMutualsButton>
                                <Button
                                    $type="white"
                                    disabled={!canUseWarmIntroAi || remainingToday <= 0 || (selectedConnections?.length === 0 && !selectedAllConnections) || isAiSelectionOverLimit || scanMutualsLoading}
                                    $customWidth
                                    $SVGtype="scan"
                                    $color={styles.colors.primary600}
                                    $iconColor={styles.colors.black300}
                                    tooltipMessage={
                                        !canUseWarmIntroAi
                                            ? 'Upgrade to Business to unlock this feature'
                                            : (selectedConnections?.length === 0 && !selectedAllConnections)
                                            ? 'Select contacts to find intros'
                                            : remainingToday <= 0
                                            ? `You\u2019ve reached your daily limit of ${dailyMax} warm intros.`
                                            : isAiSelectionOverLimit
                                            ? `You can find intros for up to ${effectiveBulkCap} contacts right now`
                                            : 'Find intros'
                                    }
                                    onClickHandler={onClickScanMutualsHandler}
                                    isLoading={selectedConnectionsLoading && scanMutualsLoading}
                                >
                                    {'Find intros'}
                                </Button>
                            </ResponsiveContainerMutualsButton>
                        ) : null}
                        <ResponsiveContainerFeedButton>
                        <Button
                            $type="white"
                            disabled={selectedConnections?.length === 0 || !(isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect)}
                            $customWidth
                            $SVGtype="feed"
                            $disabledSVGtype="feedDisabled"
                            $color={styles.colors.primary600}
                            $iconColor={styles.colors.black300}
                            tooltipMessage={
                                selectedConnections?.length === 0
                                    ? 'Select contacts to add to feed'
                                    : !(extensionInstalled && isExtensionVersionCorrect)
                                    ? 'Install or update the LeadDelta extension to use this feature'
                                    : !isLoggedToLinkedin
                                    ? 'Log in to your LinkedIn account to use this feature'
                                    : 'Add contacts to feed'
                            }
                            onClickHandler={async () => {
                                if (selectedAllConnections) {
                                    const rows = await getSelectedConnections(total, true, false);
                                    const ids = connectionIdsFromRows(rows);
                                    if (!ids.length) {
                                        showInfoToast({ message: 'Could not load all selected contacts. Try again.' });
                                        return;
                                    }
                                    setCreateFeedModal(ids);
                                    return;
                                }
                                setCreateFeedModal(selectedConnections?.map(c => c._id));
                            }}
                        >
                            {'Add to feed'}
                        </Button>
                        </ResponsiveContainerFeedButton>
                        <ResponsiveContainerSmallDesktop>
                            <Button
                                $type="white"
                                disabled={bulkRemoveDisabled && !announceBulkDisconnect}
                                $customWidth
                                $color={styles.colors.red600}
                                $iconColor={styles.colors.black300}
                                $SVGtype="profileRemove"
                                tooltipMessage={
                                    announceBulkDisconnect
                                    ? ''
                                    : user1stDegreeSelectedConnections?.length === 0
                                    ? 'Select contacts to schedule for removal'
                                    : !(extensionInstalled && isExtensionVersionCorrect)
                                    ? 'Install or update the LeadDelta extension to use this feature'
                                    : !isLoggedToLinkedin
                                    ? 'Log in to your LinkedIn account to use this feature'
                                    : 'Select contacts to schedule for removal'
                                }
                                onClickHandler={clickOnRemoveHandler}
                                isLoading={selectedConnectionsLoading && removeLoading}
                            >
                                {'Remove'}
                            </Button>
                            <Button
                                $type="white"
                                disabled={selectedConnections?.length === 0}
                                $customWidth
                                $SVGtype="label"
                                $color={styles.colors.primary600}
                                $iconColor={styles.colors.black300}
                                tooltipMessage="Tag contacts"
                                onClickHandler={() => setBulkEditTagsModal(selectedConnections?.map((c) => c._id), selectedAllConnections)}
                            >
                                {'Tag'}
                            </Button>
                        </ResponsiveContainerSmallDesktop>
                        <ActionDropdownContainer>
                            <BulkConnectionActionsDropdown selectedConnections={selectedConnections} selectedAllConnections={selectedAllConnections} onClearSelection={onClearSelection} />
                        </ActionDropdownContainer>
                        <ResponsiveContainerBigDesktop>
                            <Button
                                $type="white"
                                disabled={bulkHideDisabled}
                                $customWidth
                                $SVGtype="hide"
                                $color={styles.colors.primary600}
                                $iconColor={styles.colors.black300}
                                tooltipMessage="Hide contacts"
                                onClickHandler={async () => {
                                    if (selectedAllConnections) {
                                        await toggleBulkHideConnectionsHandler([], true);
                                    } else {
                                        await toggleBulkHideConnectionsHandler(selectedConnections, true);
                                    }
                                    if (onClearSelection) onClearSelection();
                                }}
                            >
                                {'Hide'}
                            </Button>
                            <Button
                                $type="white"
                                disabled={bulkUnhideDisabled}
                                $customWidth
                                $SVGtype="show"
                                $color={styles.colors.primary600}
                                $iconColor={styles.colors.black300}
                                tooltipMessage="Unhide contacts"
                                onClickHandler={() =>
                                    toggleBulkHideConnectionsHandler(
                                        selectedAllConnections ? [] : selectedConnections,
                                        false,
                                    )}
                            >
                                {'Unhide'}
                            </Button>
                            <Button
                                $type="white"
                                disabled={selectedConnections?.length === 0 || isStarterPlan}
                                $bigButton
                                $SVGtype="kanban"
                                $disabledSVGtype="kanbanDisabled"
                                $color={styles.colors.primary600}
                                $iconColor={styles.colors.black300}
                                tooltipMessage={isStarterPlan ? "Upgrade your plan to unlock this feature" : "Add contacts to pipeline"}
                                onClickHandler={async () => {
                                    if (isStarterPlan) return;

                                    const kanbanIds = workspace?.kanbans || [];

                                    // If there's exactly one pipeline in this workspace, add directly
                                    if (kanbanIds.length === 1) {
                                        await bulkAddConnectionsToKanbanHandler(selectedConnections, [kanbanIds[0]]);
                                        return;
                                    }

                                    // Otherwise, open modal to choose pipelines
                                    setAddToKanbanModal(selectedConnections);
                                }}
                            >
                                {'Add to pipeline'}
                            </Button>
                            <Divider />
                            <StyledRow gap="10px">
                                <Button
                                    $type="white"
                                    $smallButton
                                    $SVGtype="import"
                                    $color={styles.colors.primary600}
                                    $iconColor={styles.colors.black300}
                                    disabled={workspace?.isStarter}
                                    tooltipMessage={workspace?.isStarter ? 'Upgrade to unlock this feature' : 'Import CSV file for contacts'}
                                    onClickHandler={() => navigate(AppRoutes.CSV_IMPORT)}
                                />
                                <Button
                                    $type="white"
                                    disabled={total === 0 || workspace?.isStarter}
                                    $smallButton
                                    $SVGtype="export"
                                    $iconColor={styles.colors.black300}
                                    tooltipMessage={workspace?.isStarter ? 'Upgrade to unlock this feature' : 'Export contacts'}
                                    onClickHandler={() => {
                                            setExportConnectionsModalHandler(
                                                exportConnections,
                                                selectedAllConnections ? total : selectedConnections?.length ?? undefined,
                                                selectedAllConnections
                                            )
                                        }
                                    }
                                />
                                {hubspotEnabled && (
                                    <Button
                                        $type="white"
                                        disabled={workspace?.isStarter}
                                        $smallButton
                                        $SVGtype="exportHubspot"
                                        $iconColor={styles.colors.black300}
                                        tooltipMessage={workspace?.isStarter ? 'Upgrade to unlock this feature' : 'Export to HubSpot'}
                                        onClickHandler={() => {
                                            setExportToHubspotModalHandler(
                                                exportToHubspot,
                                                selectedAllConnections ? total : selectedConnections?.length ?? undefined,
                                                selectedAllConnections
                                            )
                                        }}
                                    />
                                )}
                            </StyledRow>
                        </ResponsiveContainerBigDesktop>
                    </>
                    }
                </ActionButtonsRow>
            </StyledRow>
            {isListView &&
                <Row gap="8px" alignItems>
                    <Row>
                        <TablePageNavigation
                            page={page ?? 1}
                            pageSize={pageSize ?? 50}
                            totalCount={total ?? 0}
                            onPageChanged={handlePageChanged}
                            onPageSizeChanged={handlePageSizeChanged}
                        />
                    </Row>
                    <StyledRow>
                        <ConnectionSearchFilter searchValue={connectionsFilter?.searchValues[0]?.searchValue} />
                    </StyledRow>
                    <Divider />
                    <Row>
                        <FilterButton
                            sidebar={SidebarTypes.CONNECTION_FILTER}
                            savedFilters={savedFilters}
                            numberOfFilters={numberOfConnectionsFiltersHandler()}
                            onSetSavedFilter={onSetSavedFilterHandler}
                            onRemoveSavedFilter={onRemoveSavedFilterHandler}
                            resetFilter={() => resetFilterHandler()}
                            onBeforeOpen={() => getMultipleConditionsFiltersHandler()}
                        />
                    </Row>
                    <ShowMobile>
                        <BulkConnectionActionsDropdown 
                            selectedConnections={selectedConnections} 
                            selectedAllConnections={selectedAllConnections}
                            onClearSelection={onClearSelection} />
                    </ShowMobile>
                </Row>
            }
            </Row>
            {isListView &&
                <Row>
                    <ConnectionFilterLabels
                        searchValues={connectionsFilter?.searchValues}
                        connectionsFilter={connectionsFilter}
                        resetFiltersHandler={resetFilterHandler}
                        saveFiltersHandler={() => openSidebar(SidebarTypes.SAVE_FILTER)} />
                </Row>
            }
            {isListView && 
                <StyledRow>
                    <QuickActions handleTagFilter={handleTagFilter} />
                </StyledRow>}
    </Column>
}

const Divider = styled.div`
    height: 18px;
    width: 2px;
    background: ${({ theme: { quinaryColor } }) => `${quinaryColor}`};

    @media (max-width: 575px) {
        display: none;
    }
`

const ActionButtonsRow = styled(Row)`
  flex-wrap: nowrap;
  flex-shrink: 0;
`

const StyledRow = styled(Row)<IRowStyle>`
    ${({showMobile}) => !showMobile && `
        @media (max-width: 575px) {
            display: none;
        }
    `}
`
const ShowMobile = styled(Row)<IRowStyle>`
    display: none;

    @media (max-width: 575px) {
        width: 100%;
        display: block;
    }
`
const ResponsiveContainerBigDesktop = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.bigDesktop}px) {
        display: none;
    }
`
const ActionDropdownContainer = styled(Row)<IRowStyle>`
    display: none;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.bigDesktop}px) {
        display: block;
    }
`
const ResponsiveContainerMutualsButton = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.mutuals}px) {
        display: none;
    }
`
const ResponsiveContainerFeedButton = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.mutuals}px) {
        display: none;
    }
`
const ResponsiveContainerSmallDesktop = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;
    gap: 8px;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.smallDesktop}px) {
        display: none;
    }
`

const ResponsiveContainerEnrichButton = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.enrich}px) {
        display: none;
    }
`

const ResponsiveContainerCampaignsButton = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.campaigns}px) {
        display: none;
    }
`

const ResponsiveContainerMessageButton = styled(Row)<IRowStyle>`
    display: flex;
    flex-direction: row;

    @media (max-width: ${CONNECTION_ACTION_BAR_BP.message}px) {
        display: none;
    }
`

export default ConnectionControlBar